import { formatDate } from "@clipboard-health/date-time";
import { useModalState } from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { ThemeProvider } from "@mui/material";
import { get } from "@src/appV2/api";
import {
  type ClipboardScoreProfileResponse,
  clipboardScoreProfileResponseSchema,
  getClipboardScoreProfileQueryKey,
} from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { useToast } from "@src/appV2/lib";
import { logError } from "@src/appV2/lib/analytics";
import { useCurrentDate } from "@src/appV2/lib/utils/useCurrentDate";
import { RestrictedBottomSheet } from "@src/appV2/redesign/ClipboardScore/components/BottomSheets/RestrictedBottomSheet";
import { EmergencyCreditsEducationContainer } from "@src/appV2/redesign/ClipboardScore/components/EmergencyCreditsEducation/EmergencyCreditsEducationContainer";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { useEmergencyCreditsConfig } from "@src/appV2/redesign/EmergencyCredits/hooks/useEmergencyCreditsConfig";
import { useCancelShift } from "@src/appV2/Shifts/Shift/ShiftState/api/useCancelShift";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import { MY_SHIFTS_FULL_PATH } from "../path";
import { useShiftCancelPageData } from "./hooks/useShiftCancelPageData";
import {
  ShiftCancelPageConfirmationScreen,
  type ShiftCancelPageConfirmedData,
} from "./ShiftCancelPageConfirmationScreen";
import { ShiftCancelPageContent } from "./ShiftCancelPageContent";

/**
 * This component is designed around emergency credits, and should only be invoked when the EC feature is enabled.
 * To avoid errors, it will gracefully degrade in the event that `emergencyCreditStats` is undefined. The resulting
 * behavior should be similar to the the standard bottom-sheet cancel flow, but has not been rigorously designed or tested.
 */
export function ShiftCancelPage(): JSX.Element {
  const { shiftId } = useParams<{ shiftId: string }>();
  const history = useHistory();
  const currentDate = useCurrentDate(60_000);
  const toast = useToast();

  const { userId, tmz } = useDefinedWorker();
  const queryClient = useQueryClient();
  const emergencyCreditsEducationModal = useModalState();
  const restrictedBottomSheetModal = useModalState();
  const emergencyCreditsConfig = useEmergencyCreditsConfig();

  const [cancellationConfirmedData, setCancellationConfirmedData] = useState<
    ShiftCancelPageConfirmedData | undefined
  >(undefined);
  const [restrictionData, setRestrictionData] = useState<{
    isFinalRestriction: boolean;
    reEnrollmentScore: number | undefined;
  }>({ isFinalRestriction: false, reEnrollmentScore: undefined });
  const [liftTime, setLiftTime] = useState<string | undefined>(undefined);
  const [isRefetchingProfile, setIsRefetchingProfile] = useState(false);

  const cancelShiftPageData = useShiftCancelPageData({
    shiftId,
    currentDate,
    enabled: !isDefined(cancellationConfirmedData),
  });

  const isBecomingRestricted = cancellationConfirmedData?.isBecomingRestricted ?? false;

  const { mutateAsync: cancelShift, isLoading: isCancelling } = useCancelShift();

  const handleKeepShift = useCallback(() => {
    history.goBack();
  }, [history]);

  const handlePressAndHoldToCancel = useCallback(async () => {
    if (
      !cancelShiftPageData.enabled ||
      cancelShiftPageData.isLoading ||
      cancelShiftPageData.isError
    ) {
      // This condition is impossible, because the button is only rendered when cancelShiftPageData is in a success state.
      // TODO Make this an `ImpossibleError`, or something that will always trigger an alert in DataDog.
      logError("ShiftCancelPage: handlePressAndHoldToCancel called in unexpected state", {
        error: new Error("cancelShiftPageData was not in a success state"),
      });
      toast.showErrorToast("The cancel button is broken. Please restart the app and try again.");
      return;
    }
    const { finalScore, isWithinECWindow, finalECStats, isFinalRestriction, reEnrollmentScore } =
      cancelShiftPageData;
    try {
      await cancelShift({
        shiftId,
        reasonType: "NONE",
        reasonDescription: "",
        isNative: false,
      });
      void queryClient.invalidateQueries({
        queryKey: getClipboardScoreProfileQueryKey(userId),
      });

      setCancellationConfirmedData({
        finalScore,
        isWithinECWindow,
        finalECStats,
        isBecomingRestricted: cancelShiftPageData.isBecomingRestricted,
        isAlreadyRestricted: cancelShiftPageData.isWorkerRestricted,
      });
      setRestrictionData({ isFinalRestriction, reEnrollmentScore });
    } catch {
      toast.showErrorToast("Something went wrong while cancelling your shift. Please try again.");
    }
  }, [cancelShift, cancelShiftPageData, queryClient, shiftId, toast, userId]);

  const handleWhyCantIUseCredit = useCallback(() => {
    // Display the EC education carousel for "Why can't I use my cancellation credit?"
    emergencyCreditsEducationModal.openModal();
  }, [emergencyCreditsEducationModal]);

  const handleConfirmationContinue = useCallback(async () => {
    if (isBecomingRestricted) {
      setIsRefetchingProfile(true);
      try {
        const queryKey = getClipboardScoreProfileQueryKey(userId);
        const profile = await queryClient.fetchQuery<ClipboardScoreProfileResponse>({
          queryKey,
          queryFn: async () => {
            const response = await get({
              url: queryKey[0],
              responseSchema: clipboardScoreProfileResponseSchema,
            });
            return response.data;
          },
        });
        const reEnrollmentDate = profile.accountStatus?.reEnrollmentDate;
        if (isDefined(reEnrollmentDate)) {
          setLiftTime(formatDate(reEnrollmentDate, { timeZone: tmz }));
        }
      } catch {
        // Graceful degradation: open bottom sheet without liftTime
      } finally {
        setIsRefetchingProfile(false);
      }
      restrictedBottomSheetModal.openModal();
    } else {
      history.push(MY_SHIFTS_FULL_PATH);
    }
  }, [history, isBecomingRestricted, queryClient, restrictedBottomSheetModal, tmz, userId]);

  const handleRestrictedContinue = useCallback(() => {
    history.push(MY_SHIFTS_FULL_PATH);
  }, [history]);

  const shouldShowConfirmationScreen = isDefined(cancellationConfirmedData);

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper sx={{ background: (theme) => theme.palette.shift?.unverified?.gradient }}>
        {!shouldShowConfirmationScreen && (
          <ShiftCancelPageContent
            {...cancelShiftPageData}
            isCancelling={isCancelling}
            onKeepShift={handleKeepShift}
            onPressAndHoldToCancel={handlePressAndHoldToCancel}
            onWhyCantIUseCredit={handleWhyCantIUseCredit}
          />
        )}

        {shouldShowConfirmationScreen && (
          <ShiftCancelPageConfirmationScreen
            {...cancellationConfirmedData}
            isLoading={isRefetchingProfile}
            onContinue={handleConfirmationContinue}
          />
        )}

        {emergencyCreditsConfig.enabled && (
          <EmergencyCreditsEducationContainer
            showCloseButton
            modalState={emergencyCreditsEducationModal}
            markAsSeen={() => {
              // No-op: This is for informational access, not onboarding
            }}
            emergencyCreditsConfig={emergencyCreditsConfig}
          />
        )}

        {isBecomingRestricted && isDefined(restrictionData.reEnrollmentScore) && (
          <RestrictedBottomSheet
            modalState={restrictedBottomSheetModal}
            isFinalRestriction={restrictionData.isFinalRestriction}
            liftTime={liftTime}
            reEnrollmentScore={restrictionData.reEnrollmentScore}
            onContinue={handleRestrictedContinue}
          />
        )}
      </PageWrapper>
    </ThemeProvider>
  );
}
